<template>
  <v-container fluid class="py-6 px-4">
    <v-card class="elevation-3">
      <!-- Header -->
      <v-toolbar dark :color="selected.length > 0 ? 'orange darken-2' : 'primary'" class="elevation-0">
        <v-icon left>mail_outline</v-icon>
        <v-toolbar-title class="font-weight-medium">
          {{ selected.length > 0 ? `${selected.length} email(s) seleccionado(s)` : 'Catálogo de Emails de Evidencia' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Acciones en Lote -->
        <template v-if="selected.length > 0">
          <v-btn small flat color="white" @click="bulkActivate" :loading="bulkActionLoading">
            <v-icon small left>check_circle</v-icon>
            Activar
          </v-btn>
          <v-btn small flat color="white" @click="bulkDeactivate" :loading="bulkActionLoading">
            <v-icon small left>block</v-icon>
            Desactivar
          </v-btn>
          <v-btn small flat color="white" @click="bulkDelete" :loading="bulkActionLoading">
            <v-icon small left>delete</v-icon>
            Eliminar
          </v-btn>
        </template>

        <template v-else>
          <v-btn outline color="white" @click="openCreateDialog">
            <v-icon left>add</v-icon>
            Nuevo Email
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on">
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-tile @click="exportToCSV">
                <v-list-tile-action>
                  <v-icon>download</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Exportar CSV</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile @click="loadStatistics">
                <v-list-tile-action>
                  <v-icon>trending_up</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Ver Estadísticas</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
        </template>
      </v-toolbar>

      <!-- Búsqueda y Filtros -->
      <v-card-text class="pa-4 grey lighten-4 border-bottom">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 md4 class="pr-3">
            <v-text-field
              v-model="searchQuery"
              label="Buscar email..."
              color="primary"
              prepend-icon="search"
              clearable
              hide-details
              @input="handleSearch"
            ></v-text-field>
          </v-flex>

          <v-flex xs12 sm6 md2 class="pr-3">
            <v-select
              v-model="filterStatus"
              :items="[
                { text: 'Todos', value: null },
                { text: 'Activos', value: true },
                { text: 'Inactivos', value: false }
              ]"
              item-text="text"
              item-value="value"
              label="Estado"
              color="primary"
              prepend-icon="filter_list"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>

          <v-flex xs12 sm6 md3 class="pr-3">
            <v-select
              v-model="sortBy"
              :items="[
                { text: 'Email', value: 'email' },
                { text: 'Fecha', value: 'created_at' },
                { text: 'Descripción', value: 'description' }
              ]"
              item-text="text"
              item-value="value"
              label="Ordenar por"
              color="primary"
              prepend-icon="sort"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>

          <v-flex xs12 sm6 md3>
            <v-select
              v-model="sortOrder"
              :items="[
                { text: 'Ascendente', value: 'asc' },
                { text: 'Descendente', value: 'desc' }
              ]"
              item-text="text"
              item-value="value"
              label="Orden"
              color="primary"
              prepend-icon="swap_vert"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>
        </v-layout>
      </v-card-text>

      <!-- Tabla de Emails -->
      <v-data-table
        v-model="selected"
        select-all
        item-key="id"
        :headers="headers"
        :items="emails"
        :loading="loading"
        :total-items="totalEmails"
        :pagination.sync="pagination"
        :rows-per-page-items="[10, 25, 50, { text: 'Todos', value: -1 }]"
        rows-per-page-text="Filas por página"
        no-data-text="No hay emails registrados"
        class="elevation-0"
      >
        <template v-slot:items="props">
          <td>
            <v-checkbox v-model="props.selected" primary hide-details></v-checkbox>
          </td>
          <td>{{ props.item.email }}</td>
          <td class="grey--text text--darken-1">{{ props.item.description || '—' }}</td>
          <td class="text-xs-center">
            <v-chip :color="props.item.is_active ? 'green' : 'grey'" text-color="white" small>
              {{ props.item.is_active ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </td>
          <td class="text-xs-center">{{ formatDate(props.item.created_at) }}</td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small class="mr-2" color="primary" v-on="on" @click="editEmail(props.item)">edit</v-icon>
              </template>
              <span>Editar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small class="mr-2" :color="props.item.is_active ? 'orange darken-2' : 'green'" v-on="on" @click="toggleEmailStatus(props.item)">
                  {{ props.item.is_active ? 'block' : 'check_circle' }}
                </v-icon>
              </template>
              <span>{{ props.item.is_active ? 'Desactivar' : 'Activar' }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small color="red" v-on="on" @click="deleteEmail(props.item)">delete</v-icon>
              </template>
              <span>Eliminar</span>
            </v-tooltip>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="text-xs-center pa-5">
            <v-icon size="60" color="grey lighten-1" class="mb-2">mail_outline</v-icon>
            <p class="grey--text">No hay emails registrados</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo Crear/Editar -->
    <v-dialog v-model="showDialog" max-width="560px" persistent scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-icon left>{{ editingEmail.id ? 'edit' : 'add' }}</v-icon>
          <v-toolbar-title>{{ editingEmail.id ? 'Editar Email' : 'Crear Email' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="editingEmail.email"
              label="Email *"
              type="email"
              color="primary"
              prepend-icon="alternate_email"
              :rules="[
                v => !!v || 'El email es requerido',
                v => /.+@.+\..+/.test(v) || 'Debe ser un email válido'
              ]"
              :error-messages="errors.email"
              @input="clearError('email')"
            ></v-text-field>

            <v-textarea
              v-model="editingEmail.description"
              label="Descripción (Opcional)"
              color="primary"
              prepend-icon="notes"
              rows="3"
              maxlength="500"
              counter="500"
              hint="Describe el propósito de este email"
              :error-messages="errors.description"
              @input="clearError('description')"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4 border-top">
          <v-spacer></v-spacer>
          <v-btn flat color="secondary" @click="closeDialog" :disabled="savingEmail">
            Cancelar
          </v-btn>
          <v-btn color="primary" :loading="savingEmail" @click="saveEmail">
            <v-icon left>{{ editingEmail.id ? 'save' : 'add' }}</v-icon>
            {{ editingEmail.id ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo Confirmar Eliminación -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error--text">
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          <p class="mb-2">¿Desactivar el email <strong>{{ emailToDelete && emailToDelete.email }}</strong>?</p>
          <p class="caption grey--text">Esta acción desactivará el registro pero no lo eliminará del sistema.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat color="secondary" @click="showDeleteDialog = false" :disabled="deletingEmail">
            Cancelar
          </v-btn>
          <v-btn color="error" :loading="deletingEmail" @click="confirmDelete">
            Desactivar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de Notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" bottom right>
      {{ snackbar.message }}
      <v-btn flat color="white" @click="snackbar.show = false">
        Cerrar
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'EvidenceEmailsManagement',
  data() {
    return {
      emails: [],
      totalEmails: 0,
      loading: false,
      searchQuery: '',
      filterStatus: null,
      sortBy: 'email',
      sortOrder: 'asc',

      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },

      headers: [
        { text: 'Email', value: 'email' },
        { text: 'Descripción', value: 'description', sortable: false },
        { text: 'Estado', value: 'is_active', align: 'center', sortable: false },
        { text: 'Creado', value: 'created_at', align: 'center' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false }
      ],

      selected: [],
      bulkActionLoading: false,

      showDialog: false,
      editingEmail: this.getEmptyEmail(),
      valid: true,
      savingEmail: false,
      errors: {},

      showDeleteDialog: false,
      emailToDelete: null,
      deletingEmail: false,

      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  watch: {
    pagination: {
      handler() {
        this.loadEmails()
      },
      deep: true
    }
  },
  mounted() {
    this.loadEmails()
  },
  methods: {
    getEmptyEmail() {
      return {
        id: null,
        email: '',
        description: ''
      }
    },
    async loadEmails() {
      this.loading = true
      try {
        const pageSize = this.pagination.rowsPerPage === -1 ? 1000 : this.pagination.rowsPerPage
        const params = new URLSearchParams({
          page: this.pagination.page,
          page_size: pageSize,
          sort_by: this.sortBy,
          order: this.sortOrder
        })

        if (this.searchQuery) {
          params.append('q', this.searchQuery)
        }
        if (this.filterStatus !== null) {
          params.append('is_active', this.filterStatus)
        }

        const response = await fetch(`https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/search/?${params}`, {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) throw new Error('Error al cargar emails')

        const data = await response.json()
        this.emails = data.results || []
        this.totalEmails = data.count || 0
      } catch (error) {
        this.showSnackbar('Error al cargar los emails', 'error')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadEmails()
    },
    handleFilterChange() {
      this.pagination.page = 1
      this.loadEmails()
    },
    openCreateDialog() {
      this.editingEmail = this.getEmptyEmail()
      this.errors = {}
      this.showDialog = true
    },
    editEmail(email) {
      this.editingEmail = { ...email }
      this.errors = {}
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.editingEmail = this.getEmptyEmail()
      this.errors = {}
    },
    async saveEmail() {
      if (this.$refs.form && !this.$refs.form.validate()) return

      this.savingEmail = true
      this.errors = {}

      try {
        const method = this.editingEmail.id ? 'PUT' : 'POST'
        const url = this.editingEmail.id
          ? `https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/${this.editingEmail.id}/`
          : 'https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/'

        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.editingEmail.email,
            description: this.editingEmail.description
          })
        })

        const data = await response.json()

        if (!response.ok) {
          this.errors = data
          throw new Error('Error al guardar')
        }

        this.showSnackbar(
          this.editingEmail.id
            ? 'Email actualizado exitosamente'
            : 'Email creado exitosamente',
          'success'
        )
        this.closeDialog()
        this.loadEmails()
      } catch (error) {
        if (!Object.keys(this.errors).length) {
          this.showSnackbar('Error al guardar el email', 'error')
        }
      } finally {
        this.savingEmail = false
      }
    },
    deleteEmail(email) {
      this.emailToDelete = email
      this.showDeleteDialog = true
    },
    async confirmDelete() {
      this.deletingEmail = true

      try {
        const response = await fetch(`https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/${this.emailToDelete.id}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) throw new Error('Error al eliminar')

        this.showSnackbar('Email desactivado exitosamente', 'success')
        this.showDeleteDialog = false
        this.emailToDelete = null
        this.loadEmails()
      } catch (error) {
        this.showSnackbar('Error al desactivar el email', 'error')
      } finally {
        this.deletingEmail = false
      }
    },
    async toggleEmailStatus(email) {
      const endpoint = email.is_active ? 'deactivate' : 'activate'

      try {
        const response = await fetch(
          `https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/${email.id}/${endpoint}/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Token ${this.$store.getters.getToken}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) throw new Error('Error al cambiar estado')

        this.showSnackbar(
          email.is_active
            ? 'Email desactivado'
            : 'Email activado',
          'success'
        )
        this.loadEmails()
      } catch (error) {
        this.showSnackbar('Error al cambiar el estado', 'error')
      }
    },
    async exportToCSV() {
      try {
        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/?format=csv', {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`
          }
        })

        if (!response.ok) throw new Error('Error al exportar')

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `emails-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        this.showSnackbar('Archivo exportado exitosamente', 'success')
      } catch (error) {
        this.showSnackbar('Error al exportar los datos', 'error')
      }
    },
    async loadStatistics() {
      try {
        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/catalog-statistics/emails/', {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`
          }
        })

        if (!response.ok) throw new Error('Error al cargar estadísticas')

        const stats = await response.json()
        this.showSnackbar(
          `Total: ${stats.total} | Activos: ${stats.active} | Inactivos: ${stats.inactive}`,
          'info'
        )
      } catch (error) {
        this.showSnackbar('Error al cargar las estadísticas', 'error')
      }
    },
    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field)
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },
    async bulkActivate() {
      await this.executeBulkAction('activate')
    },
    async bulkDeactivate() {
      await this.executeBulkAction('deactivate')
    },
    async bulkDelete() {
      await this.executeBulkAction('delete')
    },
    async executeBulkAction(action) {
      this.bulkActionLoading = true

      try {
        const emailIds = this.selected.map(e => e.id)

        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/evidence-email-catalog/bulk_action/', {
          method: 'POST',
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email_ids: emailIds,
            action: action,
            reason: 'Acción en lote desde panel de administración'
          })
        })

        const data = await response.json()

        if (!response.ok) throw new Error(data.message || 'Error en acción en lote')

        this.showSnackbar(data.message || `${data.updated_count} email(s) actualizado(s)`, 'success')
        this.selected = []
        this.loadEmails()
      } catch (error) {
        this.showSnackbar(error.message || 'Error al ejecutar acción en lote', 'error')
      } finally {
        this.bulkActionLoading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}
</style>
